import React, { memo } from "react";
import classNames from "classnames";
import _ from "lodash";

interface Props {
	totalCount: number;
	currentPage: number; // 1-indexed
	perPage: number;
	maximumPagesInWidget?: number;
	onPageChange: (page: number) => void;
	onPerPageChange: (perPage: number) => void;
}

/**
 * Client-side pagination for the property Forms tab. Forms are loaded in full
 * via redux (no server pagination), so this is a controlled presentational
 * component that mirrors the Documents tab pagination (components/Shared/Tcp/
 * Pagination) — same markup, class names and "Show 10 20 50 100 at a time"
 * control — for a consistent look, without requiring a Datasource.
 */
const FormsPagination = ({
	totalCount,
	currentPage,
	perPage,
	maximumPagesInWidget = 10,
	onPageChange,
	onPerPageChange,
}: Props) => {
	const totalPage = Math.ceil(totalCount / perPage);

	let displayedFirstPage = 1;
	let displayedLastPage = totalPage;
	if (displayedLastPage > maximumPagesInWidget) {
		if (currentPage > Math.ceil(maximumPagesInWidget / 2)) {
			displayedFirstPage = currentPage - Math.ceil(maximumPagesInWidget / 2);
		}
		if (displayedFirstPage + maximumPagesInWidget <= displayedLastPage) {
			displayedLastPage = maximumPagesInWidget + displayedFirstPage;
		}
	}
	const displayedPages = _.range(displayedFirstPage, displayedLastPage + 1);

	if (totalCount <= 1) return null;

	const PageItem = ({ page, label, icon, active }: any) => (
		<li className={`page-item ${active ? "active" : ""}`}>
			<a
				className="page-link"
				href={`#page-${page}`}
				aria-label={label}
				onClick={(e) => {
					e.preventDefault();
					onPageChange(page);
				}}
			>
				{icon != null ? icon : page}
			</a>
		</li>
	);

	return (
		<nav className="pagination" aria-label="Page Navigation">
			{totalPage > 1 && (
				<ul>
					<PageItem key="first" page={1} label="Go to First Page" icon={<i className="fas fa-angle-double-left" />} />
					{currentPage !== displayedFirstPage && (
						<PageItem key="prev" page={currentPage - 1} label="Go to Previous Page" icon={<i className="fas fa-angle-left" />} />
					)}
					{displayedPages.map((page) => (
						<PageItem key={page} page={page} label={`Go to Page ${page}`} active={page === currentPage} />
					))}
					{currentPage !== displayedLastPage && (
						<PageItem key="next" page={currentPage + 1} label="Go to Next Page" icon={<i className="fas fa-angle-right" />} />
					)}
					<PageItem key="last" page={totalPage} label="Go to Last Page" icon={<i className="fas fa-angle-double-right" />} />
				</ul>
			)}
			{totalPage === 1 && <p>Showing all {totalCount}</p>}
			{totalPage > 1 && (
				<p>
					Showing {(currentPage - 1) * perPage + 1} -{" "}
					{currentPage * perPage > totalCount ? totalCount : currentPage * perPage} of {totalCount}
				</p>
			)}
			<p>
				Show{" "}
				{[10, 20, 50, 100].map((l) => [
					<a
						href="#updateItemsPerPage"
						className={classNames({ active: perPage === l })}
						key={`perPage-${l}`}
						onClick={(e) => {
							e.preventDefault();
							onPerPageChange(l);
							onPageChange(1);
						}}
					>
						{l}
					</a>,
					" ",
				])}
				at a time
			</p>
		</nav>
	);
};

export default memo(FormsPagination);
